import React, { useEffect, useMemo, useState } from 'react';

import { Box, Button, Stack, Typography } from '@mui/material';

import { regions } from '@luxuryescapes/lib-regions/lib/regions';

import Image from '~/components/Common/Image';
import { TRENDING_PLACES_BRANDS } from '~/components/Content/TrendingPlaces/TrendingPlacesPage';

import SearchService from '~/services/SearchService';

type ExistingImage = {
  imageId: string;
  assignments: Array<string>;
};

type Props = {
  placeId?: string;
  selectedImageId: string | null;
  onSelectImage: (imageId: string) => void;
};

const SCAN_CONCURRENCY = 16;

function ExistingTrendingPlaceImages({ placeId, selectedImageId, onSelectImage }: Props) {
  const [existingImages, setExistingImages] = useState<Array<ExistingImage>>([]);
  const [hasIncompleteScan, setHasIncompleteScan] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const allRegionCodes = useMemo(() => regions.luxuryescapes.map((region) => region.code), []);
  const allBrandValues = useMemo(() => TRENDING_PLACES_BRANDS.map((brand) => brand.value), []);

  useEffect(() => {
    if (!placeId) {
      setExistingImages([]);
      setHasIncompleteScan(false);
      setMessage(null);
      return;
    }

    let cancelled = false;
    const abortController = new AbortController();

    const loadExistingImages = async () => {
      setExistingImages([]);
      setHasIncompleteScan(false);
      setMessage('Checking every market and brand for existing images…');

      const scopes = allRegionCodes.flatMap((region) => allBrandValues.map((brand) => ({ brand, region })));
      const imageAssignments = new Map<string, Array<string>>();
      let failedScopeCount = 0;

      for (let start = 0; start < scopes.length; start += SCAN_CONCURRENCY) {
        const responses = await Promise.all(
          scopes.slice(start, start + SCAN_CONCURRENCY).map(async ({ brand, region }) => {
            try {
              const { result } = await SearchService.getTrendingPlace(brand, region, abortController.signal);
              return { brand, region, sourcePlace: result.find((item) => item.placeId === placeId) };
            } catch {
              return { brand, region, failed: true };
            }
          }),
        );

        if (cancelled) {
          return;
        }

        responses.forEach((response) => {
          if ('failed' in response) {
            failedScopeCount += 1;
            return;
          }

          if (!response.sourcePlace?.imageId) {
            return;
          }

          const market = regions.luxuryescapes.find((item) => item.code === response.region)?.name;
          const brand = TRENDING_PLACES_BRANDS.find((item) => item.value === response.brand)?.name;
          const assignments = imageAssignments.get(response.sourcePlace.imageId) ?? [];

          assignments.push(`${market ?? response.region} — ${brand ?? response.brand}`);
          imageAssignments.set(response.sourcePlace.imageId, assignments);
        });
      }

      if (cancelled) {
        return;
      }

      const images = Array.from(imageAssignments, ([imageId, assignments]) => ({ imageId, assignments }));

      setExistingImages(images);
      setMessage(
        failedScopeCount
          ? `${images.length} existing image${images.length === 1 ? '' : 's'} found, but ${failedScopeCount} market or brand lookup${failedScopeCount === 1 ? '' : 's'} failed. Results may be incomplete; try again before uploading a new image.`
          : images.length
            ? `${images.length} existing image${images.length === 1 ? '' : 's'} found. Choose one to reuse.`
            : 'This place has no image assigned in any market or brand.',
      );
      setHasIncompleteScan(failedScopeCount > 0);
    };

    loadExistingImages().catch(() => {
      if (!cancelled) {
        setMessage('Could not load existing images. You can still upload one instead.');
      }
    });

    return () => {
      cancelled = true;
      abortController.abort();
    };
  }, [allBrandValues, allRegionCodes, placeId]);

  if (!placeId) {
    return null;
  }

  return (
    <Box>
      <Typography component="h3" variant="subtitle1" gutterBottom>
        Choose an existing image to reuse
      </Typography>
      {message && (
        <Typography
          color={hasIncompleteScan || !existingImages.length ? 'warning.main' : 'text.secondary'}
          variant="body2"
        >
          {message}
        </Typography>
      )}
      <Stack direction="column" gap={2} sx={{ mt: 2 }}>
        {existingImages.map((existingImage) => (
          <Box
            key={existingImage.imageId}
            border={1}
            borderColor={selectedImageId === existingImage.imageId ? 'primary.main' : 'divider'}
            borderRadius={1}
            display="flex"
            alignItems="center"
            gap={2}
            p={2}
          >
            <Image options={{ width: 120, dpr: 2 }} publicId={existingImage.imageId} loading="lazy" />
            <Box flex={1} minWidth={0}>
              <Typography variant="subtitle2">Already assigned to</Typography>
              <Typography variant="body2">{existingImage.assignments.join(', ')}</Typography>
            </Box>
            <Button
              variant={selectedImageId === existingImage.imageId ? 'contained' : 'outlined'}
              onClick={() => onSelectImage(existingImage.imageId)}
            >
              {selectedImageId === existingImage.imageId ? 'Selected' : 'Use image'}
            </Button>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

export default ExistingTrendingPlaceImages;
